/**
 * Persisted audio settings (master volumes for music and SFX). Loaded once at
 * boot, mutated by the settings dialog, applied to the singleton audio
 * instances and saved to localStorage on every change.
 */

export interface AudioSettings {
  musicVolume: number; // 0..1
  sfxVolume: number;   // 0..1
  /** Convenience global mute toggle (separate from per-channel volume). */
  muted: boolean;
}

const KEY = 'gierka:audio-settings:v1';

const DEFAULTS: AudioSettings = {
  musicVolume: 0.5,
  sfxVolume: 0.6,
  muted: false,
};

export function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AudioSettings>;
      return {
        musicVolume: clamp01(parsed.musicVolume ?? DEFAULTS.musicVolume),
        sfxVolume: clamp01(parsed.sfxVolume ?? DEFAULTS.sfxVolume),
        muted: parsed.muted ?? DEFAULTS.muted,
      };
    }
  } catch {
    // ignore parse / quota errors
  }
  return { ...DEFAULTS };
}

export function saveSettings(s: AudioSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // ignore quota errors
  }
}

function clamp01(n: number): number {
  if (typeof n !== 'number' || Number.isNaN(n)) return 0.5;
  return Math.max(0, Math.min(1, n));
}
